import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLButton } from "OLButton";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useImports } from "api/imports";
import { DragDropInput } from "components/FormControls/DragDropInput";
import { OLModal } from "components/Tooltips/OLModal";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { ImportTypes, ImportTypesUnion } from "types/import";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    error: {
      color: theme.red500,
      display: "block",
      fontSize: 14,
      marginBottom: theme.spacing(8),
      marginTop: theme.spacing(5),
      "& + &": {
        marginTop: 0,
      },
    },
    dragDropWrapper: {
      margin: 0,
    },
    dragDropText: {
      color: theme.gray400,
      fontSize: 13,
      opacity: "0.8",
    },
    fullWidth: {
      margin: 0,
      maxWidth: "100%",
      width: "100%",
    },
    modalButton: {
      marginTop: theme.spacing(12),
    },
  }),
);

interface Props {
  isModalOpen: boolean;
  closeModal: () => void;
  type?: ImportTypesUnion;
  navigate?: () => void;
}

export const ImportXmlModal = ({
  isModalOpen,
  closeModal,
  type,
  navigate,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const { importMembers } = useImports();

  /*
    below methods prepared for the future 
    Transaction and Custom events implementation
  */
  const prepareHeader = (type?: ImportTypesUnion) => {
    switch (type) {
      case ImportTypes.MEMBER:
        return message({ id: "importMembers" });
      default:
        return "";
    }
  };

  const prepareMessage = (type?: ImportTypesUnion) => {
    switch (type) {
      case ImportTypes.MEMBER:
        return message({ id: "membersImported" });
      default:
        return "";
    }
  };

  const prepareDescription = (type?: ImportTypesUnion) => {
    switch (type) {
      case ImportTypes.MEMBER:
        return message({ id: "importMembersInfo" });
      default:
        return "";
    }
  };

  return (
    <OLModal
      header={prepareHeader(type)}
      onClose={() => closeModal()}
      open={isModalOpen}
      data-test-id="import-xml-modal"
    >
      <Formik
        initialStatus={{}}
        initialValues={{ import: undefined }}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          setSubmitting(true);
          setStatus({});
          try {
            const response = await importMembers(values.import?.[0]);
            if (response) {
              closeModal();
              response.data?.importId && notify(prepareMessage(type));
              navigate?.();
            }
          } catch (exception) {
            setSubmitting(false);
            notify(getErrorMessage(exception));
            const errors = backendErrors(getBackendErrors(exception));
            setStatus(errors);
            focusError();
          }
        }}
      >
        {({ handleSubmit, isSubmitting, setFieldValue, status, values }) => {
          const formError = status && (status[""] || status["file"]);

          return (
            <Form onSubmit={handleSubmit}>
              {formError && <span className={classes.error}>{formError}</span>}
              <DragDropInput
                data-test-id="import-xml-modal"
                className={classes.dragDropWrapper}
                description={
                  <span className={classes.dragDropText}>
                    {prepareDescription(type)}
                  </span>
                }
                header={message({
                  id: "uploadFileFromComputer",
                })}
                inputClassName={classes.fullWidth}
                singleFile
                onChange={(acceptedFiles, rejectedFiles) => {
                  setFieldValue(
                    "import",
                    acceptedFiles.length > 0
                      ? [acceptedFiles[0]]
                      : [rejectedFiles[0].file],
                  );
                }}
                onDelete={() => setFieldValue("import", undefined)}
                value={values.import}
              />
              <OLButton
                className={classes.modalButton}
                color="primary"
                disabled={isSubmitting || !values.import}
                fullWidth
                type="submit"
                variant="contained"
                data-test-id="import-xml-modal-import-button"
              >
                {message({ id: "import" })}
              </OLButton>
            </Form>
          );
        }}
      </Formik>
    </OLModal>
  );
};
